import type { PayOrderPrintContent, TextEntity } from "@/api/business/types/printing"
import type {
  ItemOrderVO as ItemOrderVOTable,
  MemberLevelDiscountInfo,
  TableItemConsumeInfo,
} from "@/api/business/types/table"
import type { OrderDetailVO } from "@/api/user/types/member"
import type { DISCOUNT_TYPE_VALUE } from "@/constants/discountType"
import type { PackageChildPrintItem } from "@/utils/packagePrintItems"
import type { TableChargePrintDetail } from "@/utils/receiptGenerator"
import type { BuildOrderTableChargePrintDetailsOptions } from "@/utils/tableChargePrintDetails"
import {
  formatBrandPointReceiptDiscountLabel,
  getReceiptItemOriginalUnitPriceCent,
  getReceiptItemQuantity,
  getReceiptItemRemark,
} from "@repo/teashop-utils"
import dayjs from "dayjs"
import { DISCOUNT_TYPE, DISCOUNT_TYPE_LABEL } from "@/constants/discountType"
import { resolveMemberBenefitNetAmount } from "@/utils/memberLevelBenefit"
import { buildPackageChildPrintItems, packageSelectionKey } from "@/utils/packagePrintItems"
import { buildOrderTableChargePrintDetails, buildTableChargeFeeSummary } from "@/utils/tableChargePrintDetails"
import { computedMinutes } from "./datetime"
import { convertCentsToYuan, mergeItems } from "./math"

export type TransformedItemOrderVOTable = Omit<ItemOrderVOTable, "salePrice"> & {
  salePrice: string
  itemPayFee: string
  itemQuantity: number
  itemTotalFee: string
  packageChildItems?: PackageChildPrintItem[]
  packageSelectionKey?: string
}

const DEFAULT_DISCOUNT_REASON = "未填写原因"

// 生成打印数据
export const madePrintInfo = (
  data: OrderDetailVO,
  payInfo: TextEntity[],
  printType: number = 0,
  tableChargeOptions: BuildOrderTableChargePrintDetailsOptions = {},
): PayOrderPrintContent => {
  try {
    const snapshot = data.orderDetailInfo?.snapshot?.snapshots
    const discountInfo = JSON.parse(data.orderInfo?.discountInfo ?? "[]")

    // 商品信息
    let productList = JSON.parse(data.orderInfo?.itemsId ?? "[]") as ItemOrderVOTable[]

    if (snapshot) {
      const tempProductList: ItemOrderVOTable[] = []
      Object.values(snapshot).forEach((item: TableItemConsumeInfo) => {
        item.orders.forEach((order: ItemOrderVOTable[]) => {
          order.forEach((singleOrder: ItemOrderVOTable) => {
            tempProductList.push(singleOrder)
          })
        })
      })
      productList = tempProductList
    }

    // 计算订单优惠金额（除去商品单独折扣，避免重复计算）
    let discountOrderAmount = 0
    // 商品单独折扣
    let discountSpecItemAmount = 0
    const discountAmount = discountInfo?.map((item: any) => {
      if (
        [
          DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_DISCOUNT,
          DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_WAIVER,
        ].includes(item.discountType)
      ) {
        discountSpecItemAmount += item.discountAmount
      } else {
        discountOrderAmount += item.discountAmount
      }
      const discountTypeLabel = DISCOUNT_TYPE_LABEL[item.discountType as DISCOUNT_TYPE_VALUE] || "优惠"
      const formattedDiscountLabel = formatBrandPointReceiptDiscountLabel(item, productList)
      const receiptDiscountLabel = formattedDiscountLabel && formattedDiscountLabel !== DEFAULT_DISCOUNT_REASON
        ? formattedDiscountLabel
        : discountTypeLabel
      return {
        name: receiptDiscountLabel,
        text: receiptDiscountLabel,
        value: "-" + (item.discountAmount / 100).toFixed(2),
      }
    })

    // 桌台信息
    const tableRecordInfo = data.tableRecordInfo ? data.tableRecordInfo[0] : undefined
    const mergeRecord = tableRecordInfo?.mergeRecord || []
    const mergeTableFee = mergeRecord.reduce((total: any, item: any) => {
      return total + item.tableFee
    }, 0)
    const tableTotalFee = ((tableRecordInfo?.tableFee + mergeTableFee) / 100).toFixed(2)
    const totalItemPrice = productList.reduce((total, item) => {
      if (item.isSend === 1) return total
      return total + getReceiptItemOriginalUnitPriceCent(item) * getReceiptItemQuantity(item)
    }, 0)
    // 订单金额（重复计算单独折扣的反向操作）
    const orderAmount = (data.orderInfo?.totalAmount ?? 0) + discountSpecItemAmount

    const transformProduct = (item: ItemOrderVOTable) => {
      const itemQuantity = getReceiptItemQuantity(item)
      const originalUnitPriceCent = getReceiptItemOriginalUnitPriceCent(item)
      let itemPayFee = "0.00"
      if (item.isSend !== 1 && !item.brandPointRedeemed) {
        if (item.discountInfo) {
          itemPayFee = convertCentsToYuan(
            itemQuantity * originalUnitPriceCent - (item?.discountInfo?.discountAmount ?? 0),
            false,
          )
        } else if (item.memberLevelDiscountInfo as MemberLevelDiscountInfo) {
          const grossAmount = itemQuantity * originalUnitPriceCent
          const netAmount = resolveMemberBenefitNetAmount(
            grossAmount,
            itemQuantity,
            item.memberLevelDiscountInfo,
          ) ?? grossAmount
          itemPayFee = convertCentsToYuan(netAmount, false)
        } else {
          itemPayFee = convertCentsToYuan(itemQuantity * originalUnitPriceCent, false)
        }
      }
      const salePrice = convertCentsToYuan(originalUnitPriceCent, false)
      const packageChildItems = buildPackageChildPrintItems(item.packageSelectionSnapshot)
      return {
        ...item,
        salePrice,
        itemPrice: salePrice,
        itemQuantity,
        itemName: item.isSend === 1 ? item.itemName + "（配）" : item.itemName,
        itemTotalFee: ((item.isSend === 1 ? 0 : itemQuantity * originalUnitPriceCent) / 100).toFixed(2),
        itemPayFee,
        remark: getReceiptItemRemark(item),
        packageChildItems,
        packageSelectionKey: packageSelectionKey(item.packageSelectionSnapshot),
      } as TransformedItemOrderVOTable
    }
    const transformPrintProductList = productList.flatMap(item => expandPackagePrintProduct(transformProduct(item)))

    let mappedData: PayOrderPrintContent & { tableChargeDetails?: TableChargePrintDetail[] }
    if (data.tableRecordInfo) {
      const orderInfoMain = [
        {
          text: "结账单号",
          name: "orderCode",
          value: data.orderInfo?.orderCode,
        },
        {
          text: "桌台名称",
          name: "tableName",
          value: tableRecordInfo?.tableName,
        },
        {
          text: "开单时间",
          name: "createTime",
          value: String(data.tableRecordInfo[0] ? data.tableRecordInfo[0].checkIn : data.orderInfo?.createTime),
        },
        {
          text: "结账时间",
          name: "orderFinishTime",
          value: dayjs(data.orderInfo?.orderFinishTime || new Date()).format("YYYY-MM-DD HH:mm:ss"),
        },
        {
          text: "消费时长",
          name: "consumeTime",
          value: computedMinutes(tableRecordInfo?.stayMinutes ?? 0),
        },
      ]

      // 只有在有会员信息时才添加会员相关项
      if (data.memberInfo?.name) {
        orderInfoMain.push({
          text: "关联会员",
          name: "memberName",
          value: data.memberInfo.name,
        })
      }

      mappedData = {
        printType,
        orderInfoMain,
        orderItems: mergeItems(transformPrintProductList),
        totalFeeInfo: [
          {
            text: "商品总额",
            value: convertCentsToYuan(totalItemPrice ?? 0, false),
          },
          {
            text: "桌台费",
            value: tableTotalFee,
          },
          {
            text: "服务费",
            name: "serviceTotalFee",
            value: convertCentsToYuan(tableRecordInfo?.serviceTotalFee ?? 0, false),
          },
          {
            text: "订单金额",
            name: "totalAmount",
            value: convertCentsToYuan(orderAmount, false),
          },
        ],
        preferentialInfo: discountAmount,
        payFee: (((data.orderInfo?.totalAmount ?? 0) - discountOrderAmount) / 100).toFixed(2),
        totalPay: ((data.orderInfo?.payAmount ?? 0) / 100).toFixed(2),
        payInfo,
      }
      if (printType === 1) {
        delete mappedData.totalPay
      }
    } else {
      mappedData = {
        printType,
        orderInfoMain: [
          {
            text: "结账单号",
            name: "orderCode",
            value: data.orderInfo?.orderCode,
          },
          {
            text: "结账时间",
            name: "orderFinishTime",
            value: String(data.orderInfo?.createTime),
          },
        ],

        orderItems: mergeItems(transformPrintProductList),
        totalFeeInfo: [
          {
            text: "商品总额",
            value: convertCentsToYuan(totalItemPrice ?? 0, false),
          },
          {
            text: "订单金额",
            name: "totalAmount",
            value: convertCentsToYuan(orderAmount, false),
          },
        ],
        preferentialInfo: discountAmount,
        payFee: (((data.orderInfo?.totalAmount ?? 0) - discountOrderAmount) / 100).toFixed(2),
        totalPay: ((data.orderInfo?.payAmount ?? 0) / 100).toFixed(2),
        payInfo,
      }
      if (printType === 1) {
        delete mappedData.totalPay
      }
    }

    // 如果是挂账，则全部应付金额都需要挂账
    if (data.orderInfo?.orderStatus == 5) {
      mappedData.pendingFee = mappedData.payFee
    }
    mappedData.orderId = data.orderInfo?.merchantOmsOrderId
    const tableChargeDetails = buildOrderTableChargePrintDetails(data, tableChargeOptions)
    if (tableChargeDetails.length) {
      mappedData.tableChargeDetails = tableChargeDetails
    }
    mappedData.totalFeeInfo = replaceTableChargeFeeSummary(mappedData.totalFeeInfo, tableChargeDetails)
    return mappedData
  } catch (e) {
    console.log("e", e)
    return {}
  }
}

const TABLE_CHARGE_TOTAL_LABELS = new Set([
  "桌台费",
  "超时费",
  "服务费",
  "包段收费",
  "超时收费",
  "计时收费",
])

function replaceTableChargeFeeSummary(
  totalFeeInfo: TextEntity[] | undefined,
  tableChargeDetails: TableChargePrintDetail[],
) {
  const summaries = buildTableChargeFeeSummary(tableChargeDetails)
  const result: TextEntity[] = []
  let summariesInserted = false
  for (const item of totalFeeInfo ?? []) {
    if (TABLE_CHARGE_TOTAL_LABELS.has(item.text ?? "")) {
      continue
    }
    if (!summariesInserted && item.text === "订单金额") {
      result.push(...summaries)
      summariesInserted = true
    }
    result.push(item)
  }
  if (!summariesInserted) {
    result.push(...summaries)
  }
  return result
}

function expandPackagePrintProduct(item: TransformedItemOrderVOTable): TransformedItemOrderVOTable[] {
  const packageChildItems = buildPackageChildPrintItems(item.packageSelectionSnapshot)
  if (!item.packageSelectionKey || !packageChildItems.length) {
    return [{ ...item, packageChildItems }]
  }

  const itemQuantity = toPositiveInteger(item.itemQuantity)
  if (itemQuantity <= 1) {
    return [{ ...item, packageChildItems }]
  }

  return Array.from({ length: itemQuantity }, (_, index) => ({
    ...item,
    count: 1,
    itemQuantity: 1,
    itemTotalFee: splitYuanAmount(item.itemTotalFee, itemQuantity, index),
    itemPayFee: splitYuanAmount(item.itemPayFee, itemQuantity, index),
    packageChildItems: packageChildItems.map(child => ({ ...child })),
  }))
}

function toPositiveInteger(value: number | string | null | undefined) {
  const quantity = Number(value ?? 0)
  return Number.isInteger(quantity) && quantity > 0 ? quantity : 0
}

function splitYuanAmount(value: number | string | null | undefined, parts: number, index: number) {
  const totalCents = Math.round(Number(value ?? 0) * 100)
  const baseCents = Math.trunc(totalCents / parts)
  const remainder = totalCents % parts
  const cents = baseCents + (index < remainder ? 1 : 0)
  return convertCentsToYuan(cents, false)
}
